import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Bell, Globe2, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

const POLL_MS = 15000;

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.connect(gain); gain.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.start(t); osc.stop(t + 0.4);
    });
  } catch { /* audio unavailable */ }
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-IN');
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seen = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setItems(data.notifications);
      setUnread(data.unread_count);
      const fresh = seen.current ? data.notifications.filter((n) => !n.read && !seen.current.has(n.id)) : [];
      seen.current = new Set(data.notifications.map((n) => n.id));
      if (fresh.length) {
        playChime();
        fresh.slice(0, 3).forEach((n) => toast.success(n.title, {
          description: n.message, duration: 10000,
          action: { label: 'View', onClick: () => navigate(n.link || '/direct-bookings') },
        }));
      }
    } catch { /* silent: retry on next poll */ }
  }, [navigate]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    navigate(n.link || '/direct-bookings');
    poll();
  };

  const readAll = async () => { await api.post('/notifications/read-all').catch(() => {}); poll(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative w-10 h-10 rounded-full flex items-center justify-center text-stone-600 hover:bg-sage-50 hover:text-sage-800 transition-colors" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-sage-700 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-stone-50 animate-in zoom-in" data-testid="notification-unread-count">{unread > 99 ? '99+' : unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-white" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="font-semibold text-stone-800">Notifications</p>
          {unread > 0 && <button onClick={readAll} className="text-xs text-sage-700 hover:underline flex items-center gap-1" data-testid="notifications-read-all"><CheckCheck className="w-3.5 h-3.5" />Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="p-6 text-center text-sm text-stone-400" data-testid="notifications-empty">No notifications yet</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 flex gap-3 border-b border-stone-50 hover:bg-stone-50 transition-colors ${n.read ? '' : 'bg-sage-50/60'}`} data-testid={`notification-item-${n.id}`}>
              <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center shrink-0"><Globe2 className="w-4 h-4 text-sage-700" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-stone-800 flex items-center gap-2">{n.title}{!n.read && <span className="w-1.5 h-1.5 rounded-full bg-sage-600" />}</p>
                <p className="text-xs text-stone-500 truncate">{n.message}</p>
                <p className="text-[11px] text-stone-400 mt-0.5">{timeAgo(n.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
